/**
 * Polling that stops when nobody is looking.
 *
 * A `setInterval` poll runs forever, including in a tab the parent
 * backgrounded days ago. Every one of those requests wakes the control
 * plane, and a warm control plane keeps its notification worker talking
 * to the database, which stops the database from ever autosuspending.
 * One forgotten tab was enough to hold the whole stack — and the bill —
 * awake around the clock.
 *
 * So: poll only while the document is visible, and refetch once on the
 * way back so returning to the tab shows fresh data rather than whatever
 * was on screen when it was hidden.
 */

import { useEffect, useRef } from 'preact/hooks';

/** True when we should be polling at all. SSR-safe. */
function documentVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState === 'visible';
}

/**
 * Run `fn` every `intervalMs`, but only while the tab is visible.
 *
 * On becoming visible again the callback fires immediately (catching up
 * on whatever was missed) and the interval restarts. On becoming hidden
 * the interval is cleared entirely — zero requests until the parent
 * comes back.
 *
 * `fn` is held in a ref, so callers don't need to memoize it; the
 * interval is never torn down just because a closure changed identity.
 */
export function useVisiblePoll(fn: () => void, intervalMs: number): void {
  const saved = useRef(fn);
  saved.current = fn;

  useEffect(() => {
    let timer: number | null = null;

    const stop = (): void => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const start = (): void => {
      stop();
      timer = window.setInterval(() => {
        saved.current();
      }, intervalMs);
    };

    const onVisibilityChange = (): void => {
      if (documentVisible()) {
        // Catch up immediately — the tab may have been hidden for hours.
        saved.current();
        start();
      } else {
        stop();
      }
    };

    if (documentVisible()) start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs]);
}
